import { Link } from "react-router-dom";
import { estimateNicheMonthly, fmtMoney } from "@/lib/earnings";

const PILLAR_KEYS = [
  ["views_to_sub", "V/S"],
  ["faceless_potential", "FCE"],
  ["evergreen_value", "EVG"],
  ["monetization_potential", "MON"],
  ["production_difficulty", "EZ"],
];

function MiniBar({ value }) {
  const pct = Math.max(8, Math.min(100, value * 10));
  return (
    <div className="h-1 bg-zinc-900 w-full overflow-hidden">
      <div
        className="h-full bg-yellow-400"
        style={{ width: `${pct}%` }}
        aria-hidden
      />
    </div>
  );
}

export default function NicheCard({ niche, onAnalyze }) {
  const score = niche.scores?.composite_score ?? 0;
  const topVideo = niche.videos?.[0];
  const hasAnalysis = Boolean(niche.analysis);
  return (
    <div
      data-testid={`niche-card-${niche.niche_id}`}
      className="group relative bg-zinc-950 border border-zinc-800 hover:border-zinc-600 rounded-sm flex flex-col overflow-hidden transition-colors"
    >
      <Link
        to={`/niches/${niche.niche_id}`}
        className="flex flex-col cursor-recon"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-900">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
            // {niche.videos?.length || 0} signals
          </span>
          <span
            className={`px-2 py-1 text-xs font-mono font-bold rounded-sm border ${
              score >= 75
                ? "border-yellow-400 text-yellow-400"
                : score >= 55
                ? "border-cyan-400 text-cyan-400"
                : "border-zinc-700 text-zinc-400"
            }`}
          >
            {score}
          </span>
        </div>

        <div className="px-4 pt-5 pb-4 flex-1">
          <h3 className="font-display text-xl font-bold text-white leading-tight mb-2 group-hover:text-yellow-400 transition-colors">
            {niche.name}
          </h3>
          <p className="text-sm text-zinc-400 leading-snug line-clamp-3">
            {niche.thesis}
          </p>
        </div>

        <div className="px-4 pb-4 grid grid-cols-5 gap-2">
          {PILLAR_KEYS.map(([k, label]) => (
            <div key={k} className="flex flex-col gap-1">
              <MiniBar value={niche.scores?.[k] ?? 0} />
              <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-600">
                {label}
              </span>
            </div>
          ))}
        </div>

        {topVideo && (
          <div className="px-4 py-3 border-t border-zinc-900 flex items-center justify-between font-mono text-[11px] text-zinc-500">
            <span className="truncate max-w-[60%]">{topVideo.channel_name}</span>
            <span className="text-yellow-400">
              {Math.round(topVideo.breakout_multiplier)}× breakout
            </span>
          </div>
        )}

        <div className="px-4 py-3 border-t border-zinc-900 flex items-center justify-between bg-yellow-400/5">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            Est. monthly earnings
          </span>
          <span
            data-testid={`niche-monthly-${niche.niche_id}`}
            className="font-mono font-bold text-yellow-400 text-sm"
          >
            {fmtMoney(estimateNicheMonthly(niche))}
            <span className="text-zinc-500 font-normal text-[10px] ml-1">/ mo</span>
          </span>
        </div>
      </Link>

      {onAnalyze && (
        <button
          data-testid={`niche-analyze-btn-${niche.niche_id}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onAnalyze(niche);
          }}
          className={`w-full py-3 font-mono text-xs uppercase tracking-[0.2em] border-t transition-colors ${
            hasAnalysis
              ? "border-yellow-400/30 text-yellow-400 bg-yellow-400/5 hover:bg-yellow-400/10"
              : "border-zinc-900 text-zinc-300 hover:text-yellow-400 hover:bg-zinc-900"
          }`}
        >
          {hasAnalysis ? "✓ View analysis" : "▸ Analyze niche"}
        </button>
      )}
    </div>
  );
}
